/**
 * Calzado Audor · el inicio. SOP: architecture/SOP-inicio.md
 *
 * 1 · EL POPUP DE LA RESEÑA (cinta e historias). Cada reseña con bota trae su cuerpo en un
 *     <template id="aud-res-…"> que Liquid ya llenó con el producto: abrir no pide nada a la red.
 * 2 · LA MOTERA agrega al carrito con el contrato de Horizon (`CartLinesUpdateEvent` de
 *     `@shopify/events`), el mismo de `aud-tallas.js`: así el cajón se abre igual (I-5).
 * 3 · LAS RESEÑAS QUE SE TURNAN bajo la motera.
 * 4 · EL MEDIDOR EN LÍNEA del bloque azul, con la guía oficial que viene en los ajustes.
 * 5 · «¿QUÉ TALLA PIDO?» abre el medidor de `aud-tallas` si la página lo tiene.
 * 6 · POR QUÉ AGUANTA: el número de la bota y su renglón se encienden juntos.
 *
 * Varias secciones cargan este archivo: un módulo se ejecuta una sola vez por página.
 */

const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------ 1 · EL POPUP */

let abierto = null; // { caja, pop, boton }

function cerrarPop() {
  if (!abierto) return;
  abierto.pop.hidden = true;
  abierto.pop.textContent = '';
  abierto.caja.classList.remove('aud-icinta--quieta');
  abierto.boton?.setAttribute('aria-expanded', 'false');
  abierto = null;
}

function abrirPop(boton) {
  const caja = boton.closest('[data-aud-resenas]');
  const pop = caja?.querySelector('[data-aud-pop]');
  const tpl = document.getElementById(`aud-res-${boton.dataset.audRes}`);
  if (!pop || !tpl) return;

  cerrarPop();
  pop.appendChild(tpl.content.cloneNode(true));
  pop.hidden = false;
  caja.classList.add('aud-icinta--quieta');
  const visible = boton.getAttribute('aria-hidden') !== 'true';
  if (visible) boton.setAttribute('aria-expanded', 'true');
  abierto = { caja, pop, boton: visible ? boton : null };

  const c = caja.getBoundingClientRect();
  const r = boton.getBoundingClientRect();
  const w = pop.offsetWidth;
  const centro = r.left + r.width / 2 - c.left;
  const izq = Math.max(12, Math.min(c.width - w - 12, centro - w / 2));
  pop.style.left = `${izq}px`;

  if (caja.hasAttribute('data-aud-pop-sobre')) {
    // En una historia el popup se posa sobre la mitad de abajo de la tarjeta: debajo quedaría fuera.
    pop.style.top = `${Math.max(0, r.bottom - c.top - pop.offsetHeight - 16)}px`;
  } else {
    pop.style.top = '';
    pop.style.setProperty('--aud-ipop-flecha', `${Math.max(18, Math.min(w - 18, centro - izq))}px`);
  }

  pop.querySelector('.aud-ipop__bota, .aud-i-btn')?.focus({ preventScroll: true });
}

document.addEventListener('click', (e) => {
  const res = e.target.closest('[data-aud-res]');
  if (res) {
    e.preventDefault();
    if (abierto && abierto.boton === res) cerrarPop();
    else abrirPop(res);
    return;
  }
  if (e.target.closest('[data-aud-pop-cerrar]')) {
    const volver = abierto?.boton;
    cerrarPop();
    volver?.focus({ preventScroll: true });
    return;
  }
  if (abierto && !e.target.closest('[data-aud-pop]')) cerrarPop();
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !abierto) return;
  const volver = abierto.boton;
  cerrarPop();
  volver?.focus({ preventScroll: true });
});

// Horizon no hace scroll en la ventana sino dentro de `.page-wrapper` (html y body van con
// overflow: hidden). Se escucha en captura para enterarse venga de donde venga el scroll.
document.addEventListener(
  'scroll',
  () => {
    if (!abierto) return;
    const r = abierto.pop.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) cerrarPop();
  },
  { passive: true, capture: true }
);

/* ------------------------------------------------------------ 2 · LA MOTERA */

async function agregar(variante, boton, error) {
  const rutas = window.Theme?.routes || window.routes || {};
  const urlAdd = rutas.cart_add_url || '/cart/add';
  const urlCart = rutas.cart_url || '/cart';

  const cuerpo = new FormData();
  cuerpo.set('id', String(variante));
  cuerpo.set('quantity', '1');
  const secciones = [...document.querySelectorAll('cart-items-component')]
    .map((x) => x.dataset.sectionId)
    .filter(Boolean);
  if (secciones.length) cuerpo.append('sections', secciones.join(','));

  let SE = null;
  try {
    SE = await import('@shopify/events');
  } catch (err) {
    /* sin el módulo se agrega igual; solo no se avisa al cajón */
  }

  const diferida = SE?.CartLinesUpdateEvent?.createPromise?.();
  if (SE && diferida) {
    boton.dispatchEvent(
      new SE.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variante), quantity: 1 }],
        promise: diferida.promise,
      })
    );
  }

  const r = await fetch(urlAdd, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: cuerpo,
  });
  const resp = await r.json();
  if (resp.status) {
    const err = new Error(resp.description || resp.message || 'No se pudo agregar');
    diferida?.reject(err);
    throw err;
  }

  const carrito = await fetch(`${urlCart}.js`).then((x) => x.json());
  diferida?.resolve({
    cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
    detail: {
      items: carrito.items,
      source: 'aud-inicio',
      sourceId: 'aud-inicio',
      itemCount: 1,
      sections: resp.sections,
      didError: false,
    },
  });
  // Sin el módulo de eventos el cajón no se entera: se va al carrito, que siempre funciona.
  if (!SE) window.location.href = urlCart;
}

for (const caja of document.querySelectorAll('[data-aud-moto]')) {
  const btn = caja.querySelector('[data-aud-moto-btn]');
  const rotulo = caja.querySelector('[data-aud-moto-talla]');
  const error = caja.querySelector('[data-aud-moto-error]');
  const textoAgregar = caja.dataset.textoAgregar || '{t}';
  let elegida = null;

  caja.addEventListener('click', async (e) => {
    const t = e.target.closest('.aud-im__tallas button');
    if (t && !t.disabled) {
      for (const o of caja.querySelectorAll('.aud-im__tallas button')) o.setAttribute('aria-pressed', String(o === t));
      elegida = { id: t.dataset.id, t: t.dataset.t };
      rotulo.textContent = elegida.t;
      btn.disabled = false;
      btn.textContent = textoAgregar.replace('{t}', elegida.t);
      error.hidden = true;
      return;
    }
    if (e.target.closest('[data-aud-moto-btn]') && elegida) {
      btn.disabled = true;
      btn.textContent = caja.dataset.textoAgregada || btn.textContent;
      try {
        await agregar(elegida.id, btn, error);
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      }
      btn.disabled = false;
      btn.textContent = textoAgregar.replace('{t}', elegida.t);
    }
  });
}

/* ------------------------------------------------------------ 3 · LAS RESEÑAS QUE SE TURNAN */

for (const fig of document.querySelectorAll('[data-aud-turnos]')) {
  const turnos = [...fig.querySelectorAll('[data-aud-turno]')];
  const pasos = [...fig.querySelectorAll('[data-aud-paso]')];
  if (turnos.length < 2) continue;
  let i = 0;
  let reloj = null;
  const ir = (n) => {
    i = n;
    turnos.forEach((x, k) => { x.hidden = k !== i; });
    pasos.forEach((x, k) => x.setAttribute('aria-current', String(k === i)));
  };
  const andar = () => {
    if (quieto) return;
    clearInterval(reloj);
    reloj = setInterval(() => ir((i + 1) % turnos.length), Number(fig.dataset.audTurnos) || 5000);
  };
  pasos.forEach((b, k) => b.addEventListener('click', () => { ir(k); andar(); }));
  fig.addEventListener('mouseenter', () => clearInterval(reloj));
  fig.addEventListener('mouseleave', andar);
  fig.addEventListener('focusin', () => clearInterval(reloj));
  andar();
}

/* ------------------------------------------------------------ 4 · EL MEDIDOR EN LÍNEA */

const num = (s) => parseFloat(String(s).trim().replace(',', '.'));

function leerGuia(texto, forma) {
  return String(texto || '')
    .split('\n')
    .map((l) => l.split('|').map((x) => x.trim()))
    .filter((c) => c[0] && !c.includes('derivada'))
    .map((c) => (forma === 'rango' ? { t: c[0], desde: num(c[1]), hasta: num(c[2]) } : { t: c[0], cm: num(c[1]) }))
    .filter((f) => (forma === 'rango' ? !isNaN(f.desde) && !isNaN(f.hasta) : !isNaN(f.cm)));
}

for (const m of document.querySelectorAll('[data-aud-medidor-linea]')) {
  let guias = {};
  try {
    guias = JSON.parse(m.querySelector('[data-aud-guias]').textContent);
  } catch (e) {
    continue;
  }
  const G = { hombre: leerGuia(guias.hombre, 'rango'), mujer: leerGuia(guias.mujer, 'punto') };
  let genero = 'hombre';
  const barra = m.querySelector('[data-aud-barra]');
  const txt = m.querySelector('[data-aud-res-txt]');

  const talla = (cm) => {
    if (genero === 'hombre') return G.hombre.find((f) => cm >= f.desde - 1e-9 && cm < f.hasta + 0.1)?.t;
    return G.mujer.find((f) => cm <= f.cm + 1e-9)?.t; // entre dos, la más grande
  };
  const pintar = () => {
    const cm = Number(barra.value);
    m.querySelector('[data-aud-cm]').textContent = cm.toFixed(1).replace('.', ',');
    const t = talla(cm);
    m.querySelector('[data-aud-talla-res]').textContent = t || '—';
    txt.textContent = t ? txt.dataset.si : txt.dataset.no;
  };
  barra.addEventListener('input', pintar);
  for (const b of m.querySelectorAll('[data-aud-g]')) {
    b.addEventListener('click', () => {
      genero = b.dataset.audG;
      for (const o of m.querySelectorAll('[data-aud-g]')) o.setAttribute('aria-pressed', String(o === b));
      pintar();
    });
  }
  pintar();
}

/* ------------------------------------------------------------ 5 · ABRIR EL MEDIDOR DE aud-tallas */

document.addEventListener('click', (e) => {
  const a = e.target.closest('[data-aud-medir]');
  // El dock trae el mismo gatillo: el que llega primero lo marca y el otro no repite.
  if (!a || e.defaultPrevented || !document.getElementById('aud-pop')) return;
  e.preventDefault();
  // aud-tallas.js escucha los clics en [data-aud-medidor]; se le pasa uno sin tocar su archivo.
  const gatillo = document.createElement('button');
  gatillo.type = 'button';
  gatillo.hidden = true;
  gatillo.setAttribute('data-aud-medidor', '');
  document.body.appendChild(gatillo);
  gatillo.click();
  gatillo.remove();
});

/* ------------------------------------------------------------ 6 · POR QUÉ AGUANTA */

for (const caja of document.querySelectorAll('[data-aud-puntos]')) {
  const todos = [...caja.querySelectorAll('[data-aud-punto]')];
  let activo = null;
  const pintar = (n, fijo) => {
    for (const el of todos) {
      const on = n !== null && el.dataset.audPunto === n;
      el.classList.toggle('is-on', on);
      if (fijo) el.setAttribute('aria-pressed', String(on));
    }
    caja.classList.toggle('is-activa', n !== null);
  };
  caja.addEventListener('click', (e) => {
    const b = e.target.closest('[data-aud-punto]');
    if (!b) return;
    activo = activo === b.dataset.audPunto ? null : b.dataset.audPunto;
    pintar(activo, true);
  });
  // En computadora, pasar el mouse por un renglón enseña su punto; al salir vuelve el elegido.
  for (const fila of caja.querySelectorAll('.aud-ia__fila')) {
    fila.addEventListener('mouseenter', () => pintar(fila.dataset.audPunto, false));
    fila.addEventListener('mouseleave', () => pintar(activo, false));
  }
}
